import { arrayMove } from '@dnd-kit/sortable';
import type { CampoApi } from '@/services/Relatorios';

export interface ConfiguracaoCamposState {
  passo: 1 | 2 | 3;
  camposVisiveis: CampoApi[];
  camposExcluidos: CampoApi[];
  selectedItems: Set<number>;
  camposSeparacao: CampoApi[];
  camposOrdenacao: CampoApi[];
  totalizadoresIncluidos: CampoApi[];
  totalizadoresExcluidos: CampoApi[];
  camposCondicaoDisponiveis: CampoApi[];
  condicaoTotalizadorCampoId: number | null;
  activeId: number | null;
}

export const initialState: ConfiguracaoCamposState = {
  passo: 1,
  camposVisiveis: [],
  camposExcluidos: [],
  selectedItems: new Set(),
  camposSeparacao: [],
  camposOrdenacao: [],
  totalizadoresIncluidos: [],
  totalizadoresExcluidos: [],
  camposCondicaoDisponiveis: [],
  condicaoTotalizadorCampoId: null,
  activeId: null,
};

type Action =
  | { type: 'INIT_CAMPOS'; visiveis: CampoApi[]; excluidos: CampoApi[]; selectedIds: Set<number> }
  | { type: 'SET_ACTIVE_ID'; id: number | null }
  | { type: 'TOGGLE_SELECTION'; campoId: number }
  | { type: 'MOVE_TO_EXCLUIDOS'; campoId: number }
  | { type: 'MOVE_TO_VISIVEIS'; campoId: number }
  | { type: 'MOVE_TO_SEPARACAO'; campoId: number }
  | { type: 'MOVE_TO_ORDENACAO'; campoId: number }
  | { type: 'MOVE_TO_TOTALIZADORES_EXCLUIDOS'; campoId: number }
  | { type: 'MOVE_TO_TOTALIZADORES_INCLUIDOS'; campoId: number }
  | {
      type: 'REORDER_LIST';
      listName:
        | 'camposVisiveis'
        | 'camposExcluidos'
        | 'camposSeparacao'
        | 'camposOrdenacao'
        | 'totalizadoresIncluidos'
        | 'totalizadoresExcluidos';
      activeId: number;
      overId: number;
    }
  | { type: 'AVANCAR_PASSO_2'; separacao: CampoApi[]; ordenacao: CampoApi[] }
  | { type: 'AVANCAR_PASSO_3'; incluidos: CampoApi[]; excluidos: CampoApi[]; visiveis: CampoApi[] }
  | { type: 'SET_CONDICAO_TOTALIZADOR'; campoId: number | null }
  | { type: 'VOLTAR' };

export function configuracaoCamposReducer(
  state: ConfiguracaoCamposState,
  action: Action
): ConfiguracaoCamposState {
  switch (action.type) {
    case 'INIT_CAMPOS':
      return {
        ...state,
        camposVisiveis: action.visiveis,
        camposExcluidos: action.excluidos,
        selectedItems: action.selectedIds,
      };
    case 'SET_ACTIVE_ID':
      return { ...state, activeId: action.id };
    case 'TOGGLE_SELECTION': {
      const next = new Set(state.selectedItems);

      if (next.has(action.campoId)) {
        next.delete(action.campoId);
      } else {
        next.add(action.campoId);
      }

      return { ...state, selectedItems: next };
    }
    case 'MOVE_TO_EXCLUIDOS': {
      const campo = state.camposVisiveis.find(item => item.Campo_id === action.campoId);
      if (!campo) return state;

      const selectedItems = new Set(state.selectedItems);
      selectedItems.delete(action.campoId);

      return {
        ...state,
        camposVisiveis: state.camposVisiveis.filter(item => item.Campo_id !== action.campoId),
        camposExcluidos: [...state.camposExcluidos, { ...campo, Configurado: 'N' }],
        selectedItems,
      };
    }
    case 'MOVE_TO_VISIVEIS': {
      const campo = state.camposExcluidos.find(item => item.Campo_id === action.campoId);
      if (!campo) return state;

      return {
        ...state,
        camposExcluidos: state.camposExcluidos.filter(item => item.Campo_id !== action.campoId),
        camposVisiveis: [...state.camposVisiveis, { ...campo, Configurado: 'S' }],
      };
    }
    case 'MOVE_TO_SEPARACAO': {
      const campo = state.camposOrdenacao.find(item => item.Campo_id === action.campoId);
      if (!campo) return state;

      return {
        ...state,
        camposOrdenacao: state.camposOrdenacao.filter(item => item.Campo_id !== action.campoId),
        camposSeparacao: [...state.camposSeparacao, campo],
      };
    }
    case 'MOVE_TO_ORDENACAO': {
      const campo = state.camposSeparacao.find(item => item.Campo_id === action.campoId);
      if (!campo) return state;

      return {
        ...state,
        camposSeparacao: state.camposSeparacao.filter(item => item.Campo_id !== action.campoId),
        camposOrdenacao: [...state.camposOrdenacao, campo],
      };
    }
    case 'MOVE_TO_TOTALIZADORES_EXCLUIDOS': {
      const campo = state.totalizadoresIncluidos.find(item => item.Campo_id === action.campoId);
      if (!campo) return state;

      return {
        ...state,
        totalizadoresIncluidos: state.totalizadoresIncluidos.filter(
          item => item.Campo_id !== action.campoId
        ),
        totalizadoresExcluidos: [...state.totalizadoresExcluidos, campo],
      };
    }
    case 'MOVE_TO_TOTALIZADORES_INCLUIDOS': {
      const campo = state.totalizadoresExcluidos.find(item => item.Campo_id === action.campoId);
      if (!campo) return state;

      return {
        ...state,
        totalizadoresExcluidos: state.totalizadoresExcluidos.filter(
          item => item.Campo_id !== action.campoId
        ),
        totalizadoresIncluidos: [...state.totalizadoresIncluidos, campo],
      };
    }
    case 'REORDER_LIST': {
      const list = state[action.listName];
      const activeIndex = list.findIndex(item => item.Campo_id === action.activeId);
      const overIndex = list.findIndex(item => item.Campo_id === action.overId);

      if (activeIndex === -1 || overIndex === -1) return state;

      return {
        ...state,
        [action.listName]: arrayMove(list, activeIndex, overIndex),
      };
    }
    case 'AVANCAR_PASSO_2':
      return {
        ...state,
        passo: 2,
        camposSeparacao: action.separacao,
        camposOrdenacao: action.ordenacao,
      };
    case 'AVANCAR_PASSO_3': {
      const condicaoAtual = action.visiveis.find(campo => campo.CondicaoTotalizador === 'S');

      return {
        ...state,
        passo: 3,
        totalizadoresIncluidos: action.incluidos,
        totalizadoresExcluidos: action.excluidos.filter(campo =>
          campo.Tipo?.toLowerCase().includes('decimal')
        ),
        camposCondicaoDisponiveis: action.visiveis,
        condicaoTotalizadorCampoId: condicaoAtual ? condicaoAtual.Campo_id : null,
      };
    }
    case 'SET_CONDICAO_TOTALIZADOR':
      return { ...state, condicaoTotalizadorCampoId: action.campoId };
    case 'VOLTAR':
      return {
        ...state,
        passo: state.passo === 3 ? 2 : state.passo === 2 ? 1 : state.passo,
      };
    default:
      return state;
  }
}
